import type { Recipe } from "@/pages/sy/models/Recipe";
import { ROW_TYPE } from "../models/RecipeTreeRow";
import type { RecipeTreeRow } from "../models/RecipeTreeRow";

/**
 * Transforms a flat array of Recipe[] (from GetRecipes SP) into a hierarchical
 * RecipeTreeRow[] suitable for ag-Grid tree data.
 *
 * Display names are computed from RecipeName (base name) + Version (calculated):
 *   Parent (IsCurrent): "RecipeName V{Version}" → "Cu-35 V3"
 *   Child (other versions): "V{Version}" → "V1", "V2"
 *
 * Structure:
 *   Parent node (IsCurrent)       → path: ["Cu-35 V3"], displayName: "Cu-35 V3"
 *     Child node (other version)  → path: ["Cu-35 V3", "V1"], displayName: "V1"
 *     Child node (other version)  → path: ["Cu-35 V3", "V2"], displayName: "V2"
 *
 * Families are grouped by OriginalRecipeId.
 * The parent is the IsCurrent version (or first version if none is current).
 * Children are ordered by Version number ascending.
 */
export const transformRecipesToTreeData = (
  recipes: Recipe[],
): RecipeTreeRow[] => {
  // Group recipes by OriginalRecipeId (family)
  const familyMap = new Map<number, Recipe[]>();

  for (const recipe of recipes) {
    const familyId = recipe.OriginalRecipeId ?? recipe.RecipeId ?? 0;
    const existing = familyMap.get(familyId) || [];
    existing.push(recipe);
    familyMap.set(familyId, existing);
  }

  const treeData: RecipeTreeRow[] = [];

  for (const [_, familyRecipes] of familyMap) {
    // Find the current version (parent) - fallback to first version if none is current
    const parentRecipe =
      familyRecipes.find((r) => r.IsCurrent) ?? familyRecipes[0];

    if (!parentRecipe) continue;

    const parentName = parentRecipe.RecipeName;

    // Create parent row (IsCurrent version)
    // Display name: "RecipeName V{Version}" (e.g., "Cu-35 V3")
    const parentDisplayName = `${parentName} V${parentRecipe.Version ?? 1}`;

    const parentRow: RecipeTreeRow = {
      id: `recipe-${parentRecipe.RecipeId}`,
      path: [parentDisplayName],
      rowType: ROW_TYPE.PARENT,
      displayName: parentDisplayName,
      RecipeId: parentRecipe.RecipeId ?? null,
      RecipeName: parentRecipe.RecipeName,
      OriginalRecipeId: parentRecipe.OriginalRecipeId ?? null,
      IsCurrent: parentRecipe.IsCurrent ?? false,
      IsUsed: parentRecipe.IsUsed ?? false,
      Version: parentRecipe.Version ?? 1,
      Author: parentRecipe.Author ?? null,
      CreationTimestamp: parentRecipe.CreationTimestamp ?? null,
      UpdateTimestamp: parentRecipe.UpdateTimestamp ?? null,
      IsEnable: parentRecipe.IsEnable,
      IsActive: parentRecipe.IsActive,
      Comments: parentRecipe.Comments ?? null,
    };

    treeData.push(parentRow);

    // Get children (non-current versions) sorted by Version ascending
    const children = familyRecipes
      .filter((r) => r.RecipeId !== parentRecipe.RecipeId)
      .sort((a, b) => (a.Version ?? 0) - (b.Version ?? 0));

    // Create child rows
    for (const recipe of children) {
      // Display name for children: "V{Version}" (e.g., "V1", "V2")
      const childDisplayName = `V${recipe.Version ?? 1}`;

      const childRow: RecipeTreeRow = {
        id: `recipe-${recipe.RecipeId}`,
        path: [parentDisplayName, childDisplayName],
        rowType: ROW_TYPE.CHILD,
        displayName: childDisplayName,
        RecipeId: recipe.RecipeId ?? null,
        RecipeName: recipe.RecipeName,
        OriginalRecipeId: recipe.OriginalRecipeId ?? null,
        IsCurrent: recipe.IsCurrent ?? false,
        IsUsed: recipe.IsUsed ?? false,
        Version: recipe.Version ?? 1,
        Author: recipe.Author ?? null,
        CreationTimestamp: recipe.CreationTimestamp ?? null,
        UpdateTimestamp: recipe.UpdateTimestamp ?? null,
        IsEnable: recipe.IsEnable,
        IsActive: recipe.IsActive,
        Comments: recipe.Comments ?? null,
      };

      treeData.push(childRow);
    }
  }

  return treeData;
};
